import { APIPropTypes, PropTypes } from 'typed/imports';
import { ComponentEventHandler, ComponentEventHandler1, ConnectedProps, PureComponent, ReactEventHandler, ReactEventHandler1, connect } from 'typed/components/utils';
import { DBBoolean, ProductPackage, RequestStatus, UserRoleBillingUserType, entities } from 'typed/api';
import { MAX_SMART_STRATEGY_PACKAGE_USER_COUNT } from 'utils/constants';
import * as untypedActions from 'actions';
import { FormattedResource } from 'typed/components/common';
import { EntityEditForm, Popup, Tooltip, User } from 'components/common';
import UserSelectionList, { TableColumn as UserSelectionListColumn } from 'typed/components/common/UserSelectionList/UserSelectionList';
import { ComponentLoader } from 'components/routing';
import './UserMultiSelect.less';

const
    VISIBLE_USERS_COUNT = 5,
    TABLE_COLUMNS = [
        UserSelectionListColumn.SELECTION,
        UserSelectionListColumn.USER,
        UserSelectionListColumn.ROLE,
        UserSelectionListColumn.TEAM,
        UserSelectionListColumn.POSITION
    ];

interface StoreProps {
    users: entities.NetworkArray<entities.UserLocal>;
}

interface OwnProps {
    className?: string;
    value?: number[];
    onlyNormalUsers?: boolean;
    onlyManagedUsers?: boolean;
    hiddenUserIDs?: number[];
    collapsable?: boolean;
    addable?: boolean;
    removable?: boolean;
    disabled?: boolean;
    onSelectionOpen?: ComponentEventHandler;
    onSelectionClose?: ComponentEventHandler;
    onChange?: ComponentEventHandler1<number[]>;
}

interface Props extends StoreProps, OwnProps, ConnectedProps {}

interface State {
    collapsed: boolean;
    temporaryValue: number[] | null;
    addUser: boolean;
}

class UserMultiSelect extends PureComponent<Props, State> {
    static readonly propTypes = {
        className: PropTypes.string,
        value: PropTypes.arrayOf(PropTypes.number),
        onlyNormalUsers: PropTypes.bool,
        onlyManagedUsers: PropTypes.bool,
        hiddenUserIDs: PropTypes.arrayOf(PropTypes.number),
        collapsable: PropTypes.bool,
        addable: PropTypes.bool,
        removable: PropTypes.bool,
        disabled: PropTypes.bool,
        onSelectionOpen: PropTypes.func,
        onSelectionClose: PropTypes.func,
        onChange: PropTypes.func,

        users: APIPropTypes.networkArrayOf(APIPropTypes.UserLocal)
    };

    static readonly defaultProps = {
        className: '',
        value: [],
        onlyNormalUsers: true,
        onlyManagedUsers: false,
        hiddenUserIDs: [],
        collapsable: true,
        addable: true,
        removable: true,
        disabled: false
    };

    state: State = {
        collapsed: true,
        temporaryValue: null,
        addUser: false
    };


    componentWillMount() {
        this.componentWillReceiveProps(this.props);
    }

    componentWillReceiveProps(nextProps: Props) {
        if (this.props.users.Custom.status === RequestStatus.UNSYNCED) {
            nextProps.dispatch(untypedActions.loadAllUsers());
        }
    }


    private _onRemoveUserClick: ReactEventHandler1<number> = (userID) => {
        if (this.props.onChange) {
            this.props.onChange(this, this.props.value!.filter(value => value !== userID));
        }
    };


    private _onExpandUsersClick: ReactEventHandler = () => {
        this.setState({ collapsed: false });
    };


    private _onSelectUsersClick: ReactEventHandler = () => {
        if (this.props.onSelectionOpen) {
            this.props.onSelectionOpen(this);
        }

        this.setState({ temporaryValue: this.props.value!.copy() });
    };

    private _onSelectUsersCancel: ComponentEventHandler = () => {
        if (this.props.onSelectionClose) {
            this.props.onSelectionClose(this);
        }

        this.setState({ temporaryValue: null });
    };

    private _onSelectedUsersChange: ComponentEventHandler1<number[]> = (_sender, value) => {
        this.setState({ temporaryValue: value });
    };

    private _onSelectedUsersSubmit: ComponentEventHandler = () => {
        if (this.props.onChange) {
            this.props.onChange(this, this.state.temporaryValue || []);
        }

        if (this.props.onSelectionClose) {
            this.props.onSelectionClose(this);
        }

        this.setState({ temporaryValue: null });
    };


    private _onAddUserClick: ReactEventHandler = () => {
        this.setState({ addUser: true });
    };

    private _onAddUserCancel: ComponentEventHandler = () => {
        this.setState({ addUser: false });
    };

    private _onNewUserSaved: ComponentEventHandler1<number> = (_sender, userID) => {
        const value = (this.state.temporaryValue || []).copy().concat(userID);

        this.setState({
            temporaryValue: value,
            addUser: false
        });
    };


    private _onSelectMeClick: ReactEventHandler = () => {
        this.setState({ temporaryValue: this.state.temporaryValue!.concat(this.props.session.UserID).unique() });
    };


    render() {
        const
            { collapsable, hiddenUserIDs, disabled, session } = this.props,
            { collapsed, temporaryValue } = this.state,

            className = this.props.className ? ` ${this.props.className}` : '',
            value = this.props.value!,

            canAddUser = (
                session.IsUserAdministrator || session.IsUser4SAdministrator ||
                session.UseUserRoleBasedBilling && session.UserRoleBillingUserTypeID &&
                session.UserRoleBillingUserTypeID !== UserRoleBillingUserType.PARTICIPANT
            ) && (
                session.ProductPackageID !== ProductPackage.SMART_STRATEGY ||
                this.props.users.Custom.status === RequestStatus.SUCCEEDED &&
                this.props.users.Data.count(user => user.HasAccessTo === DBBoolean.TRUE && !user.AdministrationUserTypeID) < MAX_SMART_STRATEGY_PACKAGE_USER_COUNT
            );

        return (
            <div className={`user-multi-select${className}`}>
                {value
                    .slice(0, collapsable && collapsed ? VISIBLE_USERS_COUNT : value.length)
                    .leftJoin(this.props.users.Data, userID => userID, ({ ID }) => ID, (userID, user) =>
                        <div key={userID} className="selected-user">
                            <User userID={userID} firstNameVisible={false} lastNameVisible={false} />

                            {
                                !disabled && this.props.removable ?
                                    <div className="remove-overlay">
                                        <button className="inline fa fa-times" onClick={this._onRemoveUserClick.bind(this, userID)} />
                                    </div> :
                                    null
                            }

                            <Tooltip className="user-multi-select-tooltip" disabled={disabled || user?.Custom.status !== RequestStatus.SUCCEEDED}>
                                <User userID={userID} pictureVisible={false} />
                                <FormattedResource
                                    resource={{ id: 'users.user_select_email' }}
                                    values={{ email: <a className="value" href={`mailto:${user?.Email || ''}`}>{user?.Email}</a> }}
                                />
                            </Tooltip>
                        </div>
                    )
                    .or(!this.props.addable ? <FormattedResource resource={{ id: 'users.no_selected_users' }} /> : null)}

                {
                    collapsable && collapsed && value.length > VISIBLE_USERS_COUNT ?
                        <button className="link show-more" onClick={this._onExpandUsersClick}>
                            <FormattedResource
                                resource={{ id: 'users.show_more_users' }}
                                values={{ count: value.length - VISIBLE_USERS_COUNT }}
                            />

                            <Tooltip><FormattedResource resource={{ id: 'newsfeed.show_all_value' }} /></Tooltip>
                        </button> :
                        null
                }

                {
                    this.props.addable ?
                        <button className={`inline user-multi-select-add${className ? `${className}-add` : ''}`} disabled={disabled} onClick={this._onSelectUsersClick}>
                            <span className="fa fa-user-plus" />
                        </button> :
                        null
                }

                {
                    temporaryValue ?
                        <Popup
                            className={`new user-multi-select-user-selection${className ? `${className}user-selection` : ''}`}
                            dimmed
                            closeMode={Popup.CloseMode.CLOSE_BUTTON}
                            onClose={this._onSelectUsersCancel}
                        >
                            <EntityEditForm
                                title={{ id: 'users.select_users' }}
                                // eslint-disable-next-line react/jsx-handler-names
                                additionalButtons={[
                                    canAddUser ? <button key="add" onClick={this._onAddUserClick}><FormattedResource resource={{ id: 'users.add_new_user' }} /></button> : null,
                                    !hiddenUserIDs?.includes(session.UserID) && !temporaryValue.includes(session.UserID) ?
                                        <button key="me" onClick={this._onSelectMeClick}><FormattedResource resource={{ id: 'users.select_me' }} /></button> :
                                        null
                                ].filter(v => v)}
                                onCancel={this._onSelectUsersCancel}
                                onSubmit={this._onSelectedUsersSubmit}
                            >
                                <div className="user-picker">
                                    <UserSelectionList
                                        fixedHeader
                                        columns={TABLE_COLUMNS}
                                        value={temporaryValue}
                                        onlyNormalUsers={this.props.onlyNormalUsers}
                                        onlyManagedUsers={this.props.onlyManagedUsers}
                                        hiddenUserIDs={this.props.hiddenUserIDs}
                                        onChange={this._onSelectedUsersChange}
                                    />
                                </div>
                            </EntityEditForm>
                        </Popup> :
                        null
                }

                {
                    this.state.addUser ?
                        <Popup className="new" dimmed closeMode={Popup.CloseMode.CLOSE_BUTTON} onClose={this._onAddUserCancel}>
                            <ComponentLoader
                                scope="user-list"
                                component="UserManagement"
                                userID={null}
                                onCancel={this._onAddUserCancel}
                                onUserSaved={this._onNewUserSaved}
                            />
                        </Popup> :
                        null
                }
            </div>
        );
    }
}

export default connect<StoreProps, OwnProps>(state => ({
    users: (state.app as any).users
}))(UserMultiSelect);
